import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ApiError, deleteInvestment } from "@/lib/api";
import type { Investment } from "@finance/shared";

interface DeleteInvestmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  investment: Investment | null;
  onDeleted: () => void;
}

export function DeleteInvestmentDialog({
  open,
  onOpenChange,
  investment,
  onDeleted,
}: DeleteInvestmentDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  async function onConfirm() {
    if (!investment) return;
    setSubmitting(true);
    setError(null);
    try {
      await deleteInvestment(investment.id);
      onDeleted();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else if (err instanceof Error) setError(err.message);
      else setError("Failed to delete investment");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete holding</DialogTitle>
          <DialogDescription>
            {investment ? (
              <>
                This permanently removes{" "}
                <span className="font-medium text-foreground">
                  {investment.name}
                </span>
                {investment.ticker ? (
                  <>
                    {" "}
                    (
                    <span className="font-mono">{investment.ticker}</span>)
                  </>
                ) : null}
                . The dashboard totals will recalculate. This cannot be undone.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <p
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={submitting || !investment}
          >
            {submitting ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
